import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useAnalyticsCaseStudy, useAnalyticsIndex } from '../hooks/useAnalytics';
import { AnalyticsFinding, FranchiseSeasonAnalytics } from '../types/analytics';
import WeeklyDecisionAnalysis from './WeeklyDecisionAnalysis';
import './Analytics.css';
import PlayerRosterAnalysis from './PlayerRosterAnalysis';
import WinImpactWaterfalls from './WinImpactWaterfalls';
import CaseStudyTldrView from './CaseStudyTldr';

const { Title, Paragraph, Text } = Typography;

const assessmentColor = (assessment: string) => {
  const normalized = assessment.toLowerCase();
  if (normalized.includes('supports') || normalized === 'confirmed') return 'volcano';
  if (normalized.includes('does not')) return 'green';
  return 'blue';
};

const metricToneClass = (tone: string) => `analytics-metric analytics-metric--${tone}`;

interface AnalyticsProps {
  slug?: string;
}

const Analytics: React.FC<AnalyticsProps> = ({ slug }) => {
  const indexQuery = useAnalyticsIndex();
  const selectedCaseStudy = slug
    ? indexQuery.data?.caseStudies.find(caseStudy => caseStudy.slug === slug)
    : indexQuery.data?.caseStudies[0];
  const caseStudyQuery = useAnalyticsCaseStudy(selectedCaseStudy?.path);
  const caseStudy = caseStudyQuery.data;

  if (indexQuery.isLoading || (selectedCaseStudy && caseStudyQuery.isLoading)) {
    return (
      <div className="analytics-page">
        <Title level={1}>Franchise Intelligence</Title>
        <LoadingSkeleton height="220px" />
        <div className="analytics-loading-grid">
          {[...Array(4)].map((_, index) => <LoadingSkeleton key={index} height="150px" />)}
        </div>
      </div>
    );
  }

  if (indexQuery.error || caseStudyQuery.error) {
    return (
      <div className="analytics-page">
        <Title level={1}>Franchise Intelligence</Title>
        <EmptyState
          message="Failed to load analytics"
          description="Regenerate the local analytics export and try again."
        />
      </div>
    );
  }

  if (!selectedCaseStudy || !caseStudy) {
    return (
      <div className="analytics-page">
        <Title level={1}>Franchise Intelligence</Title>
        <EmptyState
          message="No case studies yet"
          description="Run the local analytics exporter to publish the first study."
        />
      </div>
    );
  }

  const caseStudyNumber = Math.max(
    1,
    (indexQuery.data?.caseStudies.findIndex(item => item.slug === caseStudy.slug) ?? 0) + 1);
  const columns: ColumnsType<FranchiseSeasonAnalytics> = [
    {
      title: 'Season',
      dataIndex: 'year',
      key: 'year',
      fixed: 'left',
      width: 82,
      render: year => <strong>{year}</strong>,
    },
    {
      title: 'Record',
      key: 'record',
      width: 100,
      render: (_, season) => `${season.wins}-${season.losses}${season.ties ? `-${season.ties}` : ''}`,
    },
    {
      title: 'Result',
      key: 'result',
      width: 105,
      render: (_, season) => season.wonChampionship
        ? 'Champion'
        : season.madeFinals
          ? 'Finalist'
          : season.madePlayoffs ? 'Playoffs' : 'Regular season',
    },
    {
      title: 'Standing',
      dataIndex: 'standing',
      key: 'standing',
      width: 95,
      render: standing => `#${standing}`,
    },
    {
      title: 'Points rank',
      dataIndex: 'pointsRank',
      key: 'pointsRank',
      width: 105,
      render: rank => `#${rank}`,
    },
    {
      title: 'All-play',
      dataIndex: 'allPlayWinPercentage',
      key: 'allPlayWinPercentage',
      width: 95,
      render: value => `${(value * 100).toFixed(1)}%`,
    },
    {
      title: 'Expected wins',
      dataIndex: 'expectedWins',
      key: 'expectedWins',
      width: 120,
      render: value => value.toFixed(1),
    },
    {
      title: 'Schedule luck',
      dataIndex: 'scheduleLuckWins',
      key: 'scheduleLuckWins',
      width: 120,
      render: value => (
        <span className={value < 0 ? 'analytics-negative-number' : 'analytics-positive-number'}>
          {value > 0 ? '+' : ''}{value.toFixed(1)}
        </span>
      ),
    },
    {
      title: 'Top-half losses',
      dataIndex: 'topHalfLosses',
      key: 'topHalfLosses',
      width: 135,
    },
    {
      title: 'Adds (rank)',
      key: 'adds',
      width: 110,
      render: (_, season) => `${season.adds} (#${season.addsLeagueRank})`,
    },
    {
      title: 'Trades (rank)',
      key: 'trades',
      width: 120,
      render: (_, season) => `${season.trades} (#${season.tradesLeagueRank})`,
    },
  ];

  return (
    <div className="analytics-page">
      <header className="analytics-hero">
        <Link to="/analytics" className="analytics-back-link">
          ← Intelligence home
        </Link>
        <div className="analytics-kicker">Case study {String(caseStudyNumber).padStart(2, '0')} · Franchise intelligence</div>

        <Title level={1}>{caseStudy.title}</Title>
        <Paragraph className="analytics-subtitle">{caseStudy.subtitle}</Paragraph>
        {caseStudy.tldr ? (
          <div className="analytics-hero-tldr">
            <div className="analytics-hero-tldr-meta">
              <Text>TL;DR assessment</Text>
              <Tag color="gold">{caseStudy.tldr.confidence}</Tag>
            </div>
            <Title level={2}>{caseStudy.tldr.verdict}</Title>
            <Paragraph>{caseStudy.tldr.summary}</Paragraph>
          </div>
        ) : (
          <>
            <div className="analytics-headline">{caseStudy.headline}</div>
            <Paragraph className="analytics-summary">{caseStudy.summary}</Paragraph>
          </>
        )}
      </header>

      {caseStudy.tldr && caseStudy.modelImpactAnalysis && (
        <>
          <CaseStudyTldrView tldr={caseStudy.tldr} model={caseStudy.modelImpactAnalysis} />
          <WinImpactWaterfalls analysis={{
            method: caseStudy.modelImpactAnalysis.modelType,
            confidence: caseStudy.modelImpactAnalysis.confidence,
            waterfalls: caseStudy.modelImpactAnalysis.waterfalls,
            notes: caseStudy.modelImpactAnalysis.notes,
          }} heading="Model SHAP waterfalls" seasonWaterfalls={caseStudy.modelImpactAnalysis.seasonWaterfalls} />
        </>
      )}

      <section className="analytics-metric-grid" aria-label="Headline metrics">
        {caseStudy.headlineMetrics.map(metric => (
          <Card key={metric.label} className={metricToneClass(metric.tone)} bordered={false}>
            <Text className="analytics-metric-label">{metric.label}</Text>
            <div className="analytics-metric-value">{metric.value}</div>
            <Text className="analytics-metric-detail">{metric.detail}</Text>
          </Card>
        ))}
      </section>
      {caseStudy.winImpactAnalysis && (
        <WinImpactWaterfalls analysis={caseStudy.winImpactAnalysis} />
      )}

      {caseStudy.playerRosterAnalysis && (
        <PlayerRosterAnalysis analysis={caseStudy.playerRosterAnalysis} subjectName={caseStudy.franchiseName} />
      )}


      <section className="analytics-section">
        <div className="analytics-section-heading">
          <div>
            <Title level={2}>What the data says</Title>
            <Paragraph>Each finding carries its assessment, confidence, and the evidence used to justify it.</Paragraph>
          </div>
          <Tag color="geekblue">{caseStudy.methodologyVersion}</Tag>
        </div>
        <div className="analytics-findings">
          {caseStudy.findings.map((finding: AnalyticsFinding) => (
            <Card key={`${finding.category}-${finding.title}`} className="analytics-finding-card">
              <div className="analytics-finding-meta">
                <span className="analytics-category">{finding.category}</span>
                <Tag color={assessmentColor(finding.assessment)}>{finding.assessment}</Tag>
              </div>
              <Title level={3}>{finding.title}</Title>
              <Paragraph>{finding.summary}</Paragraph>
              <div className="analytics-evidence-grid">
                {finding.evidence.map(evidence => (
                  <div className="analytics-evidence" key={evidence.label}>
                    <span>{evidence.label}</span>
                    <strong>{evidence.value}</strong>
                    <small>{evidence.context}</small>
                  </div>
                ))}
              </div>
              <div className="analytics-confidence">Confidence: {finding.confidence}</div>
            </Card>
          ))}
        </div>
      </section>

      {caseStudy.weeklyDecisionAnalysis && (
        <WeeklyDecisionAnalysis analysis={caseStudy.weeklyDecisionAnalysis} />
      )}

      <section className="analytics-section">
        <Title level={2}>Season-by-season evidence</Title>
        <Paragraph>
          Activity ranks are league ranks for that season. Schedule luck is actual wins minus the
          all-play expectation; negative values indicate fewer wins than weekly scoring implied.
        </Paragraph>
        <Table
          className="analytics-table"
          rowKey="year"
          columns={columns}
          dataSource={caseStudy.seasons}
          pagination={false}
          scroll={{ x: 1080 }}
          size="middle"
        />
      </section>

      <section className="analytics-methodology">
        <details>
          <summary>Methodology and limitations</summary>
          <div className="analytics-methodology-columns">
            <div>
              <Title level={4}>Methodology</Title>
              <ul>{caseStudy.methodology.map(item => <li key={item}>{item}</li>)}</ul>
            </div>
            <div>
              <Title level={4}>Caveats</Title>
              <ul>{caseStudy.caveats.map(item => <li key={item}>{item}</li>)}</ul>
            </div>
          </div>
        </details>
        <Text type="secondary">
          Generated {new Date(caseStudy.generatedAt).toLocaleString()} from the local SQL archive.
        </Text>
      </section>
    </div>
  );
};

export default Analytics;
